import { extractCiOps } from "./index.js";
import { collectWindowOps, groupActivity, distinctKey } from "../../src/build/index.js";
import { describeState, reconstructState } from "../../src/build/reconstruct.js";

export const extract = extractCiOps;
const RANK = { answered: 3, unrecorded: 1 };
export const informedness = (op) => (RANK[op.outcome] || 0) * 10000 + (op.verdict ? 500 : 0) + (op.output?.length || 0);
export const collect = (prompts) => collectWindowOps(prompts, { informedness });

// The WORKFLOW. Run ids are minted per push and mean nothing a week later.
export const groupBy = (op) => op.workflow || distinctKey("unnamed workflow", op);

// The verdict vocabulary is the forge's, identical across repositories, so the
// last verdict seen per workflow accumulates.
export const stateOf = (flat) => reconstructState(flat, {
  target: groupBy,
  at: (op) => op.ts,
  read: (op) => (op.verdict ? { verdict: op.verdict } : null),
  change: (op) => (op.kind === "acted" ? "re-ran or cancelled it" : null),
});

export const build = (flat) => {
  const recon = stateOf(flat);
  const byTarget = new Map(recon.targets.map((t) => [t.target, t]));
  const built = grouped(flat);
  for (const g of built.groups) {
    const e = byTarget.get(g.key);
    if (!e) continue;
    g.state = describeState(e, { fields: ["verdict"] });
    g.changes = e.changes.map((c) => ({ what: c.what, at: c.at, checked: c.checked, stateDiffered: c.stateDiffered }));
    g.unchecked = e.unchecked;
    if (g.state) g.labels = [g.state.split(" — ")[0], ...g.labels];
    if (e.unchecked) g.labels = [`${e.unchecked} action never re-checked`, ...g.labels];
  }
  return built;
};

const grouped = (flat) => groupActivity(flat, {
  key: groupBy,
  label: (op) => op.workflow,
  seed: () => ({ kinds: {}, outcomes: {}, labels: [], runs: new Set(), inferred: 0 }),
  fold: (g, op) => {
    g.kinds[op.kind] = (g.kinds[op.kind] || 0) + 1;
    g.outcomes[op.verdict || "no verdict"] = (g.outcomes[op.verdict || "no verdict"] || 0) + 1;
    for (const r of op.runs || []) g.runs.add(r);
    if (op.namedInResult) g.inferred += 1;
  },
  finish: (g) => {
    g.labels = [
      g.runs.size > 1 ? `${g.runs.size} runs` : null,
      // Named only by the RESULT: the agent asked about the pipeline, not this
      // workflow, and the forge is what brought it up.
      g.inferred === g.ops.length ? "surfaced by the forge, not asked for" : null,
    ].filter(Boolean);
    g.runs = g.runs.size;
  },
});

export const anchor = () => "workflow";
export const ignore = ["output", "steps", "checks", "runs"];
